import { LocalDate } from "@js-joda/core";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type {
  TimeReportsV2Filter,
  TimeReportsV2Group,
  TimeReportsV2SortBy,
} from "services/api/company/timeReports.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTimeReportsV2Store } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store";
import { formatLocalDateAsApiDate } from "web-apps/company/utils/timeAndDate";
import type {
  TimeReportsV2ListQuery,
  TimeReportsV2SummaryQuery,
} from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import type { TimeReportsGroupByOption } from "../../../toolbar/timeReportsToolbar.component";

interface UseTimeReportsPersonViewParams {
  monthDate: LocalDate;
  groupByOption: TimeReportsGroupByOption;
  workGroupIds: number[];
  workerIds: number[];
  filters?: TimeReportsV2Filter[];
  dateRange: { fromDate: LocalDate; toDate: LocalDate } | null;
  isPreferencesHydrated: boolean;
}

interface UseTimeReportsPersonViewState {
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  rows: TimeReportsV2Group[];
  total: number;
  hasMore: boolean;
  loadMoreRef: RefObject<HTMLDivElement>;
  reload: () => void;
}

const PAGE_SIZE = 50;

const resolveSortBy = (
  groupByOption: TimeReportsGroupByOption,
): TimeReportsV2SortBy => {
  return groupByOption === "team" ? "WorkGroup" : "Worker";
};

const normalizeFilters = (
  filters: TimeReportsV2Filter[] | undefined,
): TimeReportsV2Filter[] | undefined => {
  if (!filters || filters.length === 0) return undefined;
  return filters;
};

const serializeNumberList = (values: number[]): string => values.join(",");

const serializeFilterList = (
  filters: TimeReportsV2Filter[] | undefined,
): string => {
  if (!filters || filters.length === 0) return "";
  return filters.join(",");
};

const useStableValueByKey = <T,>(value: T, key: string): T => {
  const cacheRef = useRef<{ key: string; value: T }>({ key, value });

  if (cacheRef.current.key !== key) {
    cacheRef.current = { key, value };
  }

  return cacheRef.current.value;
};

export const useTimeReportsPersonView = (
  params: UseTimeReportsPersonViewParams,
): UseTimeReportsPersonViewState => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [timeReportsV2State, timeReportsV2Actions] = useTimeReportsV2Store();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const wasLoadMoreIntersectingRef = useRef(false);
  const companyId = companyState.company?.id;
  const workGroupIdsKey = useMemo(
    () => serializeNumberList(params.workGroupIds),
    [params.workGroupIds],
  );
  const workerIdsKey = useMemo(
    () => serializeNumberList(params.workerIds),
    [params.workerIds],
  );
  const filtersKey = useMemo(
    () => serializeFilterList(params.filters),
    [params.filters],
  );
  const stableWorkGroupIds = useStableValueByKey(
    params.workGroupIds,
    workGroupIdsKey,
  );
  const stableWorkerIds = useStableValueByKey(params.workerIds, workerIdsKey);
  const stableFilters = useStableValueByKey(
    normalizeFilters(params.filters),
    filtersKey,
  );

  const effectiveFromDate = useMemo<LocalDate>(() => {
    return params.dateRange?.fromDate ?? params.monthDate.withDayOfMonth(1);
  }, [params.dateRange, params.monthDate]);

  const effectiveToDate = useMemo<LocalDate>(() => {
    return (
      params.dateRange?.toDate ??
      params.monthDate.withDayOfMonth(params.monthDate.lengthOfMonth())
    );
  }, [params.dateRange, params.monthDate]);

  const listQuery = useMemo<TimeReportsV2ListQuery>(() => {
    return {
      fromDate: formatLocalDateAsApiDate(effectiveFromDate),
      toDate: formatLocalDateAsApiDate(effectiveToDate),
      sortBy: resolveSortBy(params.groupByOption),
      filters: stableFilters,
      workGroupIds: stableWorkGroupIds,
      workerIds: stableWorkerIds,
      pageSize: PAGE_SIZE,
    };
  }, [
    effectiveFromDate,
    effectiveToDate,
    params.groupByOption,
    stableFilters,
    stableWorkGroupIds,
    stableWorkerIds,
  ]);

  const summaryQuery = useMemo<TimeReportsV2SummaryQuery>(() => {
    return {
      fromDate: formatLocalDateAsApiDate(effectiveFromDate),
      toDate: formatLocalDateAsApiDate(effectiveToDate),
      workGroupIds: stableWorkGroupIds,
      workerIds: stableWorkerIds,
    };
  }, [effectiveFromDate, effectiveToDate, stableWorkGroupIds, stableWorkerIds]);

  const rows = timeReportsV2State.list?.items ?? [];
  const total = timeReportsV2State.list?.total ?? 0;
  const hasMore = rows.length < total;

  useEffect(() => {
    if (!companyId) {
      timeReportsV2Actions.resetList();
      return;
    }
    if (!params.isPreferencesHydrated) {
      return;
    }
    timeReportsV2Actions.setListQuery(listQuery);
    void timeReportsV2Actions.fetchList(companyId, listQuery, 0, false);
  }, [
    companyId,
    listQuery,
    params.isPreferencesHydrated,
    timeReportsV2Actions,
  ]);

  useEffect(() => {
    if (!companyId) {
      timeReportsV2Actions.setSummaryQuery(null);
      return;
    }
    if (!params.isPreferencesHydrated) {
      return;
    }
    timeReportsV2Actions.setSummaryQuery(summaryQuery);
    void timeReportsV2Actions.fetchSummary(companyId, summaryQuery);
  }, [
    companyId,
    params.isPreferencesHydrated,
    summaryQuery,
    timeReportsV2Actions,
  ]);

  const handleLoadMore = useCallback((): void => {
    if (!companyId || !hasMore || timeReportsV2State.isListLoading) return;
    if (timeReportsV2State.isListLoadingMore) return;

    void timeReportsV2Actions.fetchList(
      companyId,
      listQuery,
      timeReportsV2State.page + 1,
      true,
    );
  }, [
    companyId,
    hasMore,
    listQuery,
    timeReportsV2Actions,
    timeReportsV2State.isListLoading,
    timeReportsV2State.isListLoadingMore,
    timeReportsV2State.page,
  ]);

  useEffect(() => {
    wasLoadMoreIntersectingRef.current = false;
  }, [listQuery]);

  useEffect(() => {
    if (
      !params.isPreferencesHydrated ||
      !loadMoreRef.current ||
      !hasMore ||
      timeReportsV2State.isListLoadingMore
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = Boolean(entries[0]?.isIntersecting);
        if (isIntersecting && !wasLoadMoreIntersectingRef.current) {
          handleLoadMore();
        }
        wasLoadMoreIntersectingRef.current = isIntersecting;
      },
      { root: null, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [
    handleLoadMore,
    hasMore,
    params.isPreferencesHydrated,
    timeReportsV2State.isListLoadingMore,
  ]);

  return {
    isLoading:
      !params.isPreferencesHydrated || timeReportsV2State.isListLoading,
    isLoadingMore: timeReportsV2State.isListLoadingMore,
    error: timeReportsV2State.listError ? t("TimeReports.LoadError") : null,
    rows,
    total,
    hasMore,
    loadMoreRef,
    reload: (): void => {
      if (!companyId) return;
      void timeReportsV2Actions.refreshAll(companyId, {
        listQuery,
        summaryQuery,
      });
    },
  };
};
